import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Toolbar } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Form, Modal, RenderFuncProps, TextField } from '@dispatch/web/ui';
import { useForm, useWatch } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ResultStatus } from '@dispatch/shared/types';

export type AdditionalDataKeyValuePair = {
  key: string;
  value: string;
};

export interface AddAdditionalDataProps {
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  cardData?: AdditionalDataKeyValuePair;
  onSave?: (value: AdditionalDataKeyValuePair) => void;
  upsertResourceStatus?: ResultStatus;
}

const formDefaults: AdditionalDataKeyValuePair = {
  key: '',
  value: '',
};

const schema = yup.object({
  key: yup.string().required('Key is required'),
  value: yup.string().required('Value is required'),
});

export const AddAdditionalData = ({
  modalOpen,
  setModalOpen,
  cardData,
  onSave,
  upsertResourceStatus,
}: AddAdditionalDataProps) => {
  const [formData, setFormData] = useState<AdditionalDataKeyValuePair>();

  useEffect(() => {
    setFormData(cardData);
  }, [cardData]);

  const handleClick = () => {
    setModalOpen(!modalOpen);
    reset(formDefaults);
    setFormData(undefined);
  };

  const {
    control: formControl,
    formState: { errors: formErrors, isDirty },
    handleSubmit,
    reset,
  } = useForm<AdditionalDataKeyValuePair>({
    resolver: yupResolver(schema),
    defaultValues: { ...formDefaults },
  });

  useEffect(() => {
    if (formData) {
      reset(formData);
    }
  }, [formData]);

  const onSaveClick = () => {
    handleSubmit((values: any) => {
      onSave?.(values);
    })();
  };

  return (
    <div data-testid="add-additionaldata-container">
      <Button variant="contained" data-testid="add-additionaldata-button">
        <div
          onClick={handleClick}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <AddIcon sx={{ fontSize: '1.25rem' }} /> Create New
        </div>
      </Button>
      <Modal
        testId="additionaldata-modal"
        open={modalOpen}
        handleClickAway={handleClick}
        title="Data"
        sx={{ width: '25vw' }}
      >
        <Form<AdditionalDataKeyValuePair>
          render={({ control, errors }: RenderFuncProps) => {
            return (
              <Stack>
                <Box sx={{ padding: '12px' }}>
                  <TextField
                    id="key"
                    name="key"
                    label="Key"
                    control={control}
                    errors={errors}
                    inputProps={{ readOnly: formData ? true : false }}
                    required
                  />
                  <TextField
                    id="value"
                    name="value"
                    label="Value"
                    control={control}
                    errors={errors}
                    sx={{ marginTop: '12px' }}
                    required
                  />
                </Box>
                <Toolbar>
                  <Box sx={{ flexGrow: 1 }} />
                  <Box>
                    <Button
                      variant="contained"
                      color="info"
                      disabled={
                        !isDirty ||
                        !isEmpty(formErrors) ||
                        upsertResourceStatus === 'loading'
                      }
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.8125rem',
                        lineHeight: '22px',
                        color: 'white',
                        letterSpacing: '0.46px',
                      }}
                      onClick={onSaveClick}
                    >
                      {upsertResourceStatus === 'loading' ? 'Saving' : 'Save'}
                    </Button>
                  </Box>
                </Toolbar>
              </Stack>
            );
          }}
          errors={formErrors}
          control={formControl}
          handleSubmit={handleSubmit}
        />
      </Modal>
    </div>
  );
};
